/**
 * 5 list pages для ПДВ-документів (Підпорція 3.1c, 2026-05-24).
 *
 * 2026-08-22 (Норма #29): Advance/PropRecalc/PropCoef — серверна
 * пагінація/сортування через InfiniteDocGrid + колонка «Організація».
 * Appendix1/2 (deprecated/read-only) лишаються на DataTable.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orgs, type Organization } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import {
  advancePayments,
  appendix1,
  appendix2,
  propRecalc,
  propCoef,
  type AdvancePayment,
  type Appendix1,
  type Appendix2,
  type PropRecalc,
  type PropCoef,
  type Appendix2Subtype,
} from '@/api/vatDocuments';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { contractors as contractorsApi } from '@/api/catalogs';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Мапа id → назва організації для колонки «Організація». */
function useOrgNameMap(): {
  orgName: Record<string, string>;
  orgItems: Organization[];
} {
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);
  return { orgName, orgItems: orgsQ.data?.items ?? [] };
}


// ============================== Advance Payments ====================


export function AdvancePaymentsPage() {
  const navigate = useNavigate();
  // Стандартні фільтри списків (2026-08-07, як /customer-orders).
  const f = useDocListFilters();
  const { orgName } = useOrgNameMap();
  // 2026-08-22 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<AdvancePayment>(
    (limit, offset, sort) => advancePayments.list({
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom?.slice(0, 10),
      date_to: f.dateTo?.slice(0, 10),
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [...f.queryKey],
  );
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);
  const cols = useMemo<ColDef<AdvancePayment>[]>(
    () => [
      {
        field: 'document_number', headerName: '№', width: 150,
        cellRenderer: (p: { data?: AdvancePayment }) => p.data ? (
          <span>
            <ImportedBadge source={p.data.external_source} />
            {p.data.external_source ? ' ' : ''}{p.data.document_number}
          </span>
        ) : '',
      },
      {
        field: 'document_date', headerName: 'Дата', width: 170,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
      },
      {
        field: 'organization_id', headerName: 'Організація', width: 200,
        valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
      },
      {
        field: 'contractor_id', headerName: 'Покупець', flex: 1, minWidth: 200,
        valueFormatter: (p) => p.value ? (cName.get(p.value) ?? '') : '',
      },
      {
        field: 'source_doc_type', headerName: 'Джерело', width: 100,
        valueFormatter: (p) => p.data
          ? (p.value === 'bank_receipt' ? 'банк' : 'каса') : '',
      },
      {
        field: 'advance_amount', headerName: 'Сума', width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'vat_amount', headerName: 'ПДВ', width: 130,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'posted', headerName: 'Проведено', width: 120,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
      },
    ],
    [cName, orgName],
  );
  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Авансові ПН<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/taxation/advance-payment-registrations/new')}>
          + Нова реєстрація
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<AdvancePayment>
        deleteRow={{ remove: (id) => advancePayments.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="advance-payment-registrations"
        onRowDoubleClicked={(r) => navigate(`/taxation/advance-payment-registrations/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}


// ============================== Appendix 1 ===========================


export function Appendix1Page() {
  const list = useQuery({
    queryKey: ['appendix-1'],
    queryFn: () => appendix1.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<Appendix1>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'parent_tax_invoice_id', header: 'Parent ПН', sortValue: (r) => r.parent_tax_invoice_id ?? '', cell: (r) => r.parent_tax_invoice_id },
      {
        key: 'total_items_count', header: 'Кільк. позицій', width: 140, align: 'right',
        sortValue: (r) => r.total_items_count,
        cell: (r) => r.total_items_count,
      },
      {
        key: 'total_amount', header: 'Сума', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmt2(r.total_amount),
      },
      {
        key: 'total_vat', header: 'ПДВ', width: 130, align: 'right',
        sortValue: (r) => Number(r.total_vat),
        cell: (r) => fmt2(r.total_vat),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаток 1 до ПН (auto-generated)</h2>
        <small style={{ color: 'var(--text-secondary)' }}>
          Автоматично створюється для ПН з більш ніж 20 позиціями
        </small>
      </div>
      <DataTable<Appendix1>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Appendix 2 ===========================
// DEPRECATED (рішення власника 2026-07-28): прибрано з меню і роутів —
// РК ведеться новим потоком (повернення → correction-from-return →
// реєстрація J1201216 у ЄРПН). Компонент/API лишаються для старих даних;
// повернути = роут у App.tsx + пункт у sidebarItems.ts.


export function Appendix2Page() {
  const [subtypeFilter, setSubtypeFilter] = useState<'ALL' | Appendix2Subtype>('ALL');
  const list = useQuery({
    queryKey: ['appendix-2', { subtypeFilter }],
    queryFn: () => appendix2.list({
      limit: 200,
      ...(subtypeFilter !== 'ALL' ? { operation_subtype: subtypeFilter } : {}),
    }),
  });
  const cols = useMemo<ColumnDef<Appendix2>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
      },
      { key: 'operation_subtype', header: 'Підтип', width: 200, sortValue: (r) => r.operation_subtype ?? '', cell: (r) => r.operation_subtype },
      { key: 'parent_tax_invoice_id', header: 'Parent ПН', sortValue: (r) => r.parent_tax_invoice_id ?? '', cell: (r) => r.parent_tax_invoice_id },
      {
        key: 'total_amount', header: 'Сума', width: 140, align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) => fmt2(r.total_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => r.posted ? 1 : 0,
        cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Додаток 2 до ПН (специфічні операції)</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label>Підтип:</label>
          <select
            value={subtypeFilter}
            onChange={(e) => setSubtypeFilter(e.target.value as 'ALL' | Appendix2Subtype)}
            data-testid="app2-subtype-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="WARRANTY_REPAIR">Гарантійний ремонт</option>
            <option value="CONSTRUCTION">Будівельні роботи</option>
            <option value="EQUIPMENT_INSTALLATION">Встановлення обладнання</option>
            <option value="OTHER">Інше</option>
          </select>
        </div>
      </div>
      <DataTable<Appendix2>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


// ============================== Proportional Recalc ==================


export function PropRecalcPage() {
  const navRecalc = useNavigate();
  // 2026-08-11 (рішення власника): орг-фільтр слідує за шапкою.
  const [orgId, setOrgId] = useOrgFilter();
  const { orgName, orgItems } = useOrgNameMap();
  // 2026-08-22 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<PropRecalc>(
    (limit, offset, sort) => propRecalc.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId],
  );
  const cols = useMemo<ColDef<PropRecalc>[]>(
    () => [
      {
        field: 'document_number', headerName: '№', width: 150,
        cellRenderer: (p: { data?: PropRecalc }) => p.data ? (
          <span>
            <ImportedBadge source={p.data.external_source} />
            {p.data.external_source ? ' ' : ''}{p.data.document_number}
          </span>
        ) : '',
      },
      { field: 'year', headerName: 'Рік', width: 100 },
      {
        field: 'document_date', headerName: 'Дата', width: 170,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
      },
      {
        field: 'organization_id', headerName: 'Організація', width: 200,
        valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
      },
      {
        field: 'coefficient_actual', headerName: 'Факт. коеф.', width: 140,
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'coefficient_used_during_year', headerName: 'Викор. коеф.', width: 150,
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'difference_amount', headerName: 'Різниця', width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'posted', headerName: 'Проведено', width: 120,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
      },
    ],
    [orgName],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          Перерахунок пропорційного ПДВ (ст.199 ПКУ)<TitleTotal total={total} />
        </h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navRecalc('/taxation/proportional-vat-recalculation/new')}>
          + Новий перерахунок
        </button>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', margin: '0.5rem 0' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {orgItems.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<PropRecalc>
        deleteRow={{ remove: (id) => propRecalc.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="prop-recalc"
        onRowDoubleClicked={(r) => navRecalc(`/taxation/proportional-vat-recalculation/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}


// ============================== Proportional Coefficient ============


export function PropCoefPage() {
  const navCoef = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const { orgName, orgItems } = useOrgNameMap();
  // 2026-08-22 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<PropCoef>(
    (limit, offset, sort) => propCoef.list({
      organization_id: orgId || undefined,
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId],
    { attachmentsType: 'doc.proportional_vat_coefficient' },
  );
  const cols = useMemo<ColDef<PropCoef>[]>(
    () => [
      {
        field: 'document_number', headerName: '№', width: 150,
        cellRenderer: (p: { data?: PropCoef }) => p.data ? (
          <span>
            <ImportedBadge source={p.data.external_source} />
            {p.data.external_source ? ' ' : ''}{p.data.document_number}
          </span>
        ) : '',
      },
      { field: 'year', headerName: 'Рік', width: 100 },
      {
        field: 'organization_id', headerName: 'Організація', width: 200,
        valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
      },
      {
        field: 'coefficient', headerName: 'Коефіцієнт', width: 140,
        cellStyle: { textAlign: 'right' },
      },
      { field: 'based_on_year', headerName: 'На основі року', width: 150 },
      { field: 'approval_date', headerName: 'Затверджено', width: 130 },
      {
        field: 'posted', headerName: 'Проведено', width: 120,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
      },
      {
        colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
        cellStyle: { textAlign: 'center' },
        cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
      },
    ],
    [orgName],
  );
  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Коефіцієнт пропорційного ПДВ<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navCoef('/taxation/proportional-vat-coefficient/new')}>
          + Новий коефіцієнт
        </button>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', margin: '0.5rem 0' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
          style={{ maxWidth: 240 }}>
          <option value="">Усі організації</option>
          {orgItems.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
      </div>
      <InfiniteDocGrid<PropCoef>
        deleteRow={{ remove: (id) => propCoef.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="prop-coef"
        onRowDoubleClicked={(r) => navCoef(`/taxation/proportional-vat-coefficient/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
